import { Box, Card, Group, Stack, UnstyledButton } from '@mantine/core';
import { IconBolt, IconChevronDown } from '@tabler/icons-react';
import { GetEvChargingSessionsResponse } from 'api/actions/get-ev-charging-sessions/get-ev-charging-sessions-response';
import { format } from 'date-fns';
import useLang from 'lang';
import theme from 'theme';
import P2Medium from 'theme/components/Typography/P2Medium';
import P2Semibold from 'theme/components/Typography/P2Semibold';
import P4Regular from 'theme/components/Typography/P4Regular';
import React, { useState } from 'react';
import { getSessionBillingLabel } from 'pages/ev-charging-station/sessionBilling';

const INITIAL_VISIBLE_COUNT = 5;

interface ChargingHistoryCardProps {
  sessions: GetEvChargingSessionsResponse['sessions'];
}

/**
 * Formats a session's duration and time range, e.g. "50 min. • from 7:05 to 7:55".
 */
function formatDurationAndRange(startedAt: string, endedAt: string, _t: (msgid: string) => string) {
  const start = new Date(startedAt);
  const end = new Date(endedAt);
  const durationMinutes = Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000));

  return `${durationMinutes} ${_t('min.')}   •   ${_t('from')} ${format(start, 'H:mm')} ${_t('to')} ${format(
    end,
    'H:mm'
  )}`;
}

/**
 * "Charging history" card: the current customer's individual charging
 * sessions at this station, most recent first.
 */
export default function ChargingHistoryCard({ sessions }: ChargingHistoryCardProps) {
  const { _t, lang, dateFnsLocale } = useLang();
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COUNT);
  const visibleSessions = sessions.slice(0, visibleCount);
  const hasMore = sessions.length > visibleCount;

  if (sessions.length === 0) {
    return (
      <Stack gap={8}>
        <Group>
          <P4Regular color="gray.7">{_t('Charging history')}</P4Regular>
        </Group>
        <Card p={16} radius={10}>
          <P4Regular color="gray.6">{_t('You have no charging sessions at this station yet.')}</P4Regular>
        </Card>
      </Stack>
    );
  }

  return (
    <Stack gap={8}>
      <Group>
        <P4Regular color="gray.7">{_t('Charging history')}</P4Regular>
      </Group>
      <Stack gap={8}>
        {visibleSessions.map((session) => (
          <Card key={session.id} p={16} radius={10}>
            <Group align="stretch" wrap="nowrap" gap={16}>
              <Box
                bg="primary.1"
                style={{
                  borderRadius: 10,
                  flexShrink: 0,
                  width: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <IconBolt size={20} color={theme.colors?.primary?.[0]} fill={theme.colors?.primary?.[0]} />
              </Box>
              <Stack gap={8} style={{ flex: 1, minWidth: 0 }}>
                <Stack gap={0}>
                  <P2Medium>{format(new Date(session.startedAt), 'd. MMMM yyyy', { locale: dateFnsLocale })}</P2Medium>
                  <P4Regular color="gray.6">{formatDurationAndRange(session.startedAt, session.endedAt, _t)}</P4Regular>
                </Stack>
                <Box style={{ borderTop: `1px solid ${theme.colors?.gray?.[2]}` }} />
                <Group justify="space-between" wrap="nowrap">
                  <P2Medium c="primary.0">
                    {session.consumptionKwh.toLocaleString(lang, { maximumFractionDigits: 1 })} kWh
                  </P2Medium>
                  <P2Medium ta="right" customStyle={{ maxWidth: '60%' }}>
                    {getSessionBillingLabel(session, _t) ||
                      (session.priceWithVat !== null
                        ? `${session.priceWithVat.toLocaleString(lang, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })} €`
                        : '—')}
                  </P2Medium>
                </Group>
              </Stack>
            </Group>
          </Card>
        ))}
      </Stack>
      {hasMore && (
        <UnstyledButton
          onClick={() => setVisibleCount((count) => count + INITIAL_VISIBLE_COUNT)}
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 4,
            padding: '8px 8px 8px 16px',
            borderRadius: 10,
            border: `1px solid ${theme.colors?.gray?.[2]}`,
          }}
        >
          <P2Semibold color="gray.7">{_t('Show older')}</P2Semibold>
          <IconChevronDown size={20} color={theme.colors?.gray?.[7]} />
        </UnstyledButton>
      )}
    </Stack>
  );
}
